/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/ClaimAssessmentWorkspace.tsx
 * FILE_ID: QUOTE-CLAIMS-G10-ASSESSMENT-001
 * PURPOSE: Manual claimed-versus-certified assessment workspace.
 * WHY: Assessors need auditable reductions, source mapping and dispute outcomes.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, CheckCircle2, Download, FileUp, Loader2, Lock, XCircle } from 'lucide-react'
import { saveBlobDownload } from '@quotech/api'
import {
  importClaimAssessmentWorkbook,
  loadClaimAssessment,
  saveClaimAssessment,
  transitionClaimAssessment,
  type ClaimAssessment,
} from '@quotech/api/g10ProgressClaims'
import { formatCurrency } from '@/lib/utils'
import type { ClaimEditorController } from './useClaimEditorController'

function statusCode(error: unknown) {
  return (error as { response?: { status?: number } })?.response?.status
}

export function ClaimAssessmentWorkspace({ controller }: { controller: ClaimEditorController }) {
  const claim = controller.claim
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<ClaimAssessment | null>(null)
  const [conflict, setConflict] = useState(false)
  const query = useQuery({
    queryKey: ['claim-assessment', claim?.id],
    queryFn: () => loadClaimAssessment(controller.api, claim!),
    enabled: Boolean(claim?.id),
    retry: false,
  })
  useEffect(() => { if (query.data) setDraft(query.data) }, [query.data])

  const save = useMutation({
    mutationFn: () => saveClaimAssessment(controller.api, draft!),
    onSuccess: async (assessment) => {
      setDraft(assessment)
      setConflict(false)
      await queryClient.invalidateQueries({ queryKey: ['claim-assessment', claim?.id] })
    },
    onError: (error) => setConflict(statusCode(error) === 409),
  })
  const transition = useMutation({
    mutationFn: (action: 'issue' | 'accept' | 'dispute' | 'resolve') =>
      transitionClaimAssessment(controller.api, draft!.id!, action, draft!.row_version),
    onSuccess: setDraft,
    onError: (error) => setConflict(statusCode(error) === 409),
  })
  const importWorkbook = useMutation({
    mutationFn: (file: File) => importClaimAssessmentWorkbook(
      controller.api,
      claim!.id,
      draft!.profile_key === 'sopa-v1' ? 'sopa-v1' : 'assessed-v1',
      file,
    ),
    onSuccess: (result) => {
      const values = new Map(result.lines.map((line) => [line.contract_line_id, line]))
      setDraft((current) => current ? {
        ...current,
        source_file_id: result.file_record_id,
        source_name: result.file_name,
        lines: current.lines.map((line) => {
          const imported = values.get(line.line_id)
          return imported ? {
            ...line,
            certified_amount: Number(imported.certified_amount),
            reduction_reason: imported.reduction_reason,
          } : line
        }),
      } : current)
    },
  })

  const totals = useMemo(() => (draft?.lines ?? []).reduce((sum, line) => ({
    claimed: sum.claimed + line.claimed_amount,
    certified: sum.certified + line.certified_amount,
  }), { claimed: 0, certified: 0 }), [draft?.lines])

  if (query.isLoading) return <div role="status" className="flex items-center justify-center gap-2 rounded-xl border p-10 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Loading assessment…</div>
  if (query.isError || !draft) return <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700"><AlertCircle className="inline h-5 w-5" /> Assessment could not be loaded. <button className="underline" onClick={() => query.refetch()}>Try again</button></div>

  const changeLine = (lineId: string, patch: Partial<ClaimAssessment['lines'][number]>) => {
    setDraft({ ...draft, lines: draft.lines.map((line) => line.line_id === lineId ? { ...line, ...patch } : line) })
  }
  const invalid = draft.lines.some((line) => line.certified_amount < 0 || line.certified_amount > line.claimed_amount || (line.certified_amount < line.claimed_amount && !line.reduction_reason.trim()))
  const download = async (format: 'pdf' | 'docx' | 'xlsx') => {
    const response = await controller.api.get(
      `/api/claims/assessments/${draft.id}/export-${format}/`,
      { responseType: 'blob' },
    )
    saveBlobDownload({
      blob: response.data as Blob,
      fallbackFilename: `assessment-${draft.reference}.${format}`,
    })
  }

  return (
    <section className="space-y-4" aria-label="Claim assessment">
      {conflict && <div role="alert" className="flex items-center justify-between rounded-lg border border-orange-300 bg-orange-50 p-3 text-sm text-orange-800"><span>Another assessor updated this claim. Reload before saving.</span><button className="font-medium underline" onClick={() => query.refetch()}>Reload</button></div>}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-4 dark:bg-slate-800">
        <div><h2 className="font-semibold">Assessment status</h2><p className="text-sm capitalize text-slate-500">{draft.status.replace('_', ' ')}</p></div>
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm"><FileUp className="h-4 w-4" /><input type="file" className="sr-only" accept=".xlsx" onChange={(event) => { const file = event.target.files?.[0]; if (file) { setDraft({ ...draft, source_name: file.name }); importWorkbook.mutate(file) } }} />Import XLSX</label>
          {controller.claim?.can_approve && draft.status === 'draft' && <button type="button" onClick={() => transition.mutate('issue')} disabled={transition.isPending || invalid || !draft.id} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white disabled:opacity-50"><Lock className="h-4 w-4" />Issue assessment</button>}
          {controller.claim?.can_approve && draft.status !== 'draft' && <button type="button" onClick={() => transition.mutate('accept')} disabled={transition.isPending || invalid || !draft.id} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-emerald-600 px-3 text-sm font-medium text-white disabled:opacity-50"><CheckCircle2 className="h-4 w-4" />Accept</button>}
          {controller.claim?.can_approve && draft.status !== 'draft' && <button type="button" onClick={() => transition.mutate('dispute')} disabled={transition.isPending || !draft.id} className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-red-300 px-3 text-sm font-medium text-red-700"><XCircle className="h-4 w-4" />Dispute</button>}
          {draft.issue_checksum && (['pdf', 'docx', 'xlsx'] as const).map((format) => <button key={format} type="button" onClick={() => void download(format)} className="inline-flex min-h-11 items-center gap-1 rounded-lg border px-3 text-sm"><Download className="h-4 w-4" />{format.toUpperCase()}</button>)}
        </div>
        <div className="grid w-full gap-3 sm:grid-cols-3">
          <label className="text-xs text-slate-500">Reference<input value={draft.reference} onChange={(event) => setDraft({ ...draft, reference: event.target.value })} className="mt-1 min-h-11 w-full rounded-lg border px-3 text-sm dark:bg-slate-900" /></label>
          <label className="text-xs text-slate-500">Assessment date<input type="date" value={draft.assessment_date} onChange={(event) => setDraft({ ...draft, assessment_date: event.target.value })} className="mt-1 min-h-11 w-full rounded-lg border px-3 text-sm dark:bg-slate-900" /></label>
          <label className="text-xs text-slate-500">Workbook profile<select value={draft.profile_key} onChange={(event) => setDraft({ ...draft, profile_key: event.target.value })} className="mt-1 min-h-11 w-full rounded-lg border px-3 text-sm dark:bg-slate-900"><option value="canonical-v1">Canonical</option><option value="assessed-v1">Assessed schedule</option><option value="sopa-v1">SOPA schedule</option></select></label>
        </div>
        {draft.source_name && <p className="w-full text-xs text-slate-500">Source selected: {draft.source_name}</p>}
      </div>

      <div className="space-y-3">
        {draft.lines.map((line) => (
          <article key={line.line_id} className="grid gap-3 rounded-xl border bg-white p-4 sm:grid-cols-[minmax(12rem,1fr)_9rem_9rem_minmax(12rem,1fr)] sm:items-end dark:bg-slate-800">
            <div><p className="font-medium">{line.description}</p><p className="text-xs text-slate-500">Line {line.line_id}</p></div>
            <div className="text-sm"><span className="block text-xs text-slate-500">Claimed</span><span className="font-mono">{formatCurrency(line.claimed_amount)}</span></div>
            <label className="text-xs text-slate-500">Certified<input aria-label={`Certified amount for ${line.description}`} type="number" min={0} max={line.claimed_amount} value={line.certified_amount} disabled={draft.status !== 'draft'} onChange={(event) => changeLine(line.line_id, { certified_amount: Number(event.target.value) })} className="mt-1 min-h-11 w-full rounded-lg border px-2 text-right font-mono disabled:bg-slate-100 dark:bg-slate-900" /></label>
            <label className="text-xs text-slate-500">Reduction reason<input value={line.reduction_reason} onChange={(event) => changeLine(line.line_id, { reduction_reason: event.target.value })} disabled={draft.status !== 'draft' || line.certified_amount >= line.claimed_amount} className="mt-1 min-h-11 w-full rounded-lg border px-3 text-sm disabled:bg-slate-100 dark:bg-slate-900" /></label>
          </article>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border bg-slate-900 p-4 text-white">
        <div className="flex gap-6 text-sm"><span>Claimed <strong className="block font-mono text-lg">{formatCurrency(totals.claimed)}</strong></span><span>Certified <strong className="block font-mono text-lg">{formatCurrency(totals.certified)}</strong></span><span>Reduction <strong className="block font-mono text-lg">{formatCurrency(totals.claimed - totals.certified)}</strong></span></div>
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending || invalid || draft.status !== 'draft'} className="min-h-11 rounded-lg bg-amber-500 px-4 font-medium text-white disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save assessment'}</button>
      </div>
      {invalid && <p role="alert" className="text-sm text-red-600">Certified amounts must be within the claimed value, and every reduction needs a reason.</p>}
    </section>
  )
}
